import { Paragraph } from '@purpurds/purpur';
import {
  TeliaCol,
  TeliaContentBanner,
  TeliaHeading,
  TeliaRow,
  TeliaTextSpacing,
} from '@teliads/components/react';
import './ContentCards.scss';

import CardContentWrapper from '../card-content-wrapper/CardContentWrapper';
import Carousel from '../carousel/Carousel';
import { CarouselVariant } from '../carousel/types';

import { BaseCardContent, ContentfulRichText } from 'utils/models';
import { renderRichText } from 'utils/rich-text-render';
import { Dimensions, createMultipleSizeImages } from 'utils/utils';

type Props = {
  title?: string;
  preamble?: ContentfulRichText;
  cardList: BaseCardContent[];
  isCarousel?: boolean;
  titleCentered?: boolean;
  cta?: {
    url: string;
    text: string;
  };
};

export const defaultContentCardsImage = {
  url: '/image/purple-900.png',
  altText: 'dark purple background',
  focusArea: 'center',
};

const ContentCards = ({
  cardList,
  title,
  preamble,
  cta,
  isCarousel = false,
  titleCentered = false,
}: Props) => {
  const contentCard: Dimensions = {
    small: ['3:2', 360],
    medium: ['3:2', 420],
    large: ['3:2', 640],
  };

  return (
    <section className="content-card-section">
      <CardContentWrapper
        title={title}
        preamble={preamble ? renderRichText(preamble.json) : undefined}
        cta={cta}
        isTitleCentered={titleCentered}
      >
        <>
          {!isCarousel && (
            <TeliaRow>
              {cardList.map((item, id) => {
                const imageSizes = createMultipleSizeImages(
                  {
                    url: item.image.url,
                    altText: item.image.altText,
                    focusArea: item.image.focusArea,
                  },
                  contentCard
                );

                return (
                  <TeliaCol
                    width={12}
                    widthMd={4}
                    key={`${item.title}-${id}`}
                    className="content-card-section__content-card"
                  >
                    <TeliaContentBanner
                      imgSrc={item.image.url ? imageSizes.large : defaultContentCardsImage.url}
                      mediumSet={item.image.url ? imageSizes.medium : defaultContentCardsImage.url}
                      smallSet={item.image.url ? imageSizes.small : defaultContentCardsImage.url}
                      imgAlt={
                        item.image.url ? item.image.altText : defaultContentCardsImage.altText
                      }
                      href={item.url || undefined}
                      dataTestid="content-card"
                    >
                      <TeliaTextSpacing>
                        <TeliaHeading tag="h2" variant="title-200">
                          {item.title || ''}
                        </TeliaHeading>
                        <Paragraph className="content-card__preamble">
                          {item.body || <></>}
                        </Paragraph>
                      </TeliaTextSpacing>
                    </TeliaContentBanner>
                  </TeliaCol>
                );
              })}
            </TeliaRow>
          )}
          {isCarousel && (
            <Carousel variant={CarouselVariant.SMALL_FRAME}>
              {cardList.map((item, id) => {
                const imageSizes = createMultipleSizeImages(
                  {
                    url: item.image.url,
                    altText: item.image.altText,
                    focusArea: item.image.focusArea,
                  },
                  contentCard
                );

                return (
                  <TeliaCol
                    width={12}
                    key={`${item.title}-${id}`}
                    className="content-card-section__content-card"
                  >
                    <TeliaContentBanner
                      imgSrc={item.image.url ? imageSizes.large : defaultContentCardsImage.url}
                      mediumSet={item.image.url ? imageSizes.medium : defaultContentCardsImage.url}
                      smallSet={item.image.url ? imageSizes.small : defaultContentCardsImage.url}
                      imgAlt={
                        item.image.url ? item.image.altText : defaultContentCardsImage.altText
                      }
                      href={item.url || undefined}
                      dataTestid="content-card"
                    >
                      <TeliaTextSpacing>
                        <TeliaHeading tag="h2" variant="title-200">
                          {item.title || ''}
                        </TeliaHeading>
                        <Paragraph className="content-card__preamble">
                          {item.body || <></>}
                        </Paragraph>
                      </TeliaTextSpacing>
                    </TeliaContentBanner>
                  </TeliaCol>
                );
              })}
            </Carousel>
          )}
        </>
      </CardContentWrapper>
    </section>
  );
};

export default ContentCards;
